import styled, { css, keyframes } from 'styled-components';
import { media } from '@simplywallst/ui-core';
import { Row, Column } from '@simplywallst/ui-core';
import { Ellipsis, EllipsisWithLineClamp } from '@simplywallst/ui-core';
import { Button } from '@simplywallst/ui-core';

export const TileWrapper = styled(Column)`
  padding: ${({ theme }) =>
    `${theme.x3spacePx}px ${theme.x1spacePx}px 0 ${theme.x1spacePx}px`};
  ${media.xsDown`
    padding: 0;
    padding-bottom: 12px;
  `};
`;

export const Wrapper = styled(Row)`
  margin-left: -${({ theme }) => theme.x1spacePx}px;
  margin-right: -${({ theme }) => theme.x1spacePx}px;
  ${media.mdUp`
    padding-bottom: ${({ theme }) => theme.x3spacePx}px;
	`};
  ${media.mdDown`
		margin-left: ${({ theme }) => theme.x1spacePx}px;
		margin-right: ${({ theme }) => theme.x1spacePx}px;
	`};
  ${media.xsDown`
		margin-left: 0;
		margin-right: 0;
	`};
`;

export const MainThumb = styled('div')<{ src: string }>`
  background-image: url(${({ src }) => src});
  background-size: cover;
  background-position: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  opacity: 0.4;
  ${media.mdUp`
    border-top-left-radius: ${({ theme }) => theme.x1spacePx}px;
    border-top-right-radius: ${({ theme }) => theme.x1spacePx}px;
  `}
`;

export const Container = styled('div')`
  position: relative;
  background: ${({ theme }) => theme.color.noir};
  &:hover {
    box-shadow: 0px 4px 16px ${({ theme }) => theme.color.blackRGBA(0.25)};
  }
  ${media.mdUp`
    border-radius: ${({ theme }) => theme.x1spacePx}px;
  `};
`;

export const TileTable = styled('table')`
  width: 100%;
  padding: 16px;
  table-layout: fixed;
  ${media.xsDown`
    padding-left: 138px;
  `};

  td {
    text-align: left;
    &:last-child {
      ${media.xsDown`
        text-align: right;
      `}
    }
  }
  th {
    ${({ theme }) => theme.typography.smallStrong}
    > span {
      display: block;
      &:first-child {
        display: inline-flex;
        width: 100%;
        justify-content: space-between;
        ${({ theme }) => theme.typography.tinyStrong}
        color: ${({ theme }) => theme.color.brand01};
        text-transform: uppercase;
        vertical-align: top;
        > a {
          ${({ theme }) => theme.typography.tiny}
          color: ${({ theme }) => theme.color.brand01};
          text-transform: initial;
          > span {
            text-align: right;
            width: 100%;
          }
        }
      }
      &:last-child {
        ${Ellipsis};
        max-width: 100%;
        margin-top: 2px;
        text-align: left;
      }
    }
  }
  td {
    > span {
      display: block;
      &:first-child {
        ${({ theme }) => theme.typography.tiny}
        opacity: 0.5;
        margin-top: ${({ theme }) => theme.x1spacePx}px;
      }
      &:last-child {
        ${({ theme }) => theme.typography.xSmallStrong}
        margin-top: 2px;
      }
    }
  }
`;

export enum Visibility {
  VISIBLE = 'visible',
  HIDDEN = 'hidden',
}

const containerBase = css`
  position: relative;
  height: 184px;
  ${media.xsDown`
    width: 130px;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 0;
  `}
`;

interface Props {
  visible: Visibility;
}

export const SnowflakeWrapper = styled('div').attrs<Props>(({ visible }) => ({
  display: visible === Visibility.VISIBLE ? 'block' : 'none',
}))<Props>`
  ${containerBase}
  > div {
    &:first-child {
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 1;
      max-width: 168px;
      margin-top: -${168 / 2}px;
      margin-left: -${168 / 2}px;
      ${media.xsDown`
        max-width: 108px;
        margin-top: -${108 / 2}px;
        margin-left: -${108 / 2}px;
      `}
    }
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 52%;
    left: 50%;
    z-index: 0;
    width: 120px;
    height: 120px;
    margin-top: -${120 / 2}px;
    margin-left: -${120 / 2}px;
    border-radius: 120px;
    ${media.xsDown`
      width: 78px;
      height: 78px;
        margin-top: -${78 / 2}px;
        margin-left: -${78 / 2}px;
      `}
    background: transparent;
    box-shadow: 0 0 20px black;
  }
`;

export const SnowflakeBone = styled('div')`
  ${containerBase};
  background: ${({ theme }) => theme.color.whiteRGBA(0.1)};
  ${media.mdUp`
    border-bottom-left-radius: ${({ theme }) => theme.x1spacePx}px;
    border-bottom-right-radius: ${({ theme }) => theme.x1spacePx}px;
  `};
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 0;
    width: 150px;
    height: 150px;
    max-width: 150px;
    margin-top: -${150 / 2}px;
    margin-left: -${150 / 2}px;
    border-radius: 168px;
    ${media.xsDown`
      max-width: 90px;
      max-height: 90px;
      margin-top: -${90 / 2}px;
      margin-left: -${90 / 2}px;
    `}
    background: ${({ theme }) => theme.color.whiteRGBA(0.05)};
  }
`;

export const Value = styled('span')<{ value: null | number; opacity?: number }>`
  color: ${({ value, theme }) =>
    theme.color.whiteRGBA(value === null ? 0.3 : 1)};
  ${({ opacity }) => opacity && `opacity: ${opacity};`}
`;

export const NoResults = styled('p')`
  text-align: center;
  padding-top: ${({ theme }) => theme.x5spacePx}px;
  ${({ theme }) => theme.typography.xSmall};
  opacity: 0.3;
`;

const fadeIn = keyframes`
  0% { opacity: 0; }
  10% { opacity: 0; }
  100% { opacity: 1; }
`;

export const Description = styled('div')<Props>`
  visibility: ${({ visible }) =>
    visible === Visibility.VISIBLE ? 'visibility' : 'hidden'};
  position: absolute;
  color: ${({ theme }) => theme.color.whiteRGBA(0.5)};
  bottom: ${({ theme }) => theme.x2spacePx}px;
  height: ${({ theme }) => theme.x20spacePx}px;
  padding: ${({ theme }) => theme.x1spacePx}px
    ${({ theme }) => theme.x2spacePx + 3}px;
  ${({ theme }) => theme.typography.xSmall}
  background: ${({ theme }) => theme.color.noir};
  z-index: 9;
  ${({ visible }) =>
    visible === Visibility.VISIBLE &&
    css`
      animation: ${fadeIn} 0.4s cubic-bezier(0.23, 1, 0.32, 1);
    `}
  ${media.mdDown`
    height: ${({ theme }) => theme.x18spacePx}px;
  `}
  ${media.xsDown`
    left: 138px;
    padding-left: 2px;
  `}
  > span {
    ${EllipsisWithLineClamp(5)}
    height: ${({ theme }) => theme.x13spacePx}px;
    ${media.mdDown`
      height: ${({ theme }) => theme.x12spacePx}px;
    `}
  }
`;

export const Actions = styled('div')`
  position: absolute;
  z-index: 9;
  right: ${({ theme }) => theme.x2spacePx}px;
  bottom: ${({ theme }) => theme.x2spacePx}px;
  ${media.xsDown`
    right: ${({ theme }) => theme.x1spacePx}px;
    bottom: ${({ theme }) => theme.x3spacePx}px;
  `}
`;

export const About = styled(Button)<{ active: boolean }>`
  ${({ active, theme }) =>
    active &&
    css`
      background-color: ${theme.color.whiteRGBA(0.1)};
    `}
  border-radius: ${({ theme }) => theme.x5spacePx}px;
  height: ${({ theme }) => theme.x5spacePx}px;
  margin-top: -4px;
  margin-bottom: -4px;
  box-shadow: none !important;
  ${media.mdUp`
    &:hover {
      background-color: ${({ theme }) => theme.color.whiteRGBA(0.1)};
    }
  `}
`;

export const CompanyName = styled('span')`
  text-align: left;
`;
